/**
 * lensThicknessMath.js — standard ophthalmic-dispensing formulas for
 * Minimum Blank Size (MBS) and edge/center thickness estimation.
 *
 * These are the same textbook formulas used across the optical dispensing
 * industry (Brooks & Borish, "System for Ophthalmic Dispensing"; Jalie,
 * "Principles of Ophthalmic Lenses") — not a proprietary approximation:
 *
 *   Effective Diameter (ED) = diagonal of the frame's boxed eye size,
 *     sqrt(A² + B²) — the largest dimension the lens blank must cover.
 *   Decentration = how far the optical center must shift from the frame's
 *     geometric center to align with the patient's actual pupil.
 *   MBS = ED + 2×decentration + edging allowance.
 *   Sag (surface depth) = R − sqrt(R² − r²), where R is the surface's
 *     radius of curvature derived from lens power and refractive index,
 *     and r is the lens radius (ED/2). This is the exact sag formula, not
 *     the small-angle r²/2R shortcut.
 *   Thickness = the chosen minimum (center for minus lenses, edge for
 *     plus lenses) + sag at the worst-case meridian.
 *
 * Cylinder is handled via spherical equivalent (SE = SPH + CYL/2), which
 * is mathematically identical regardless of plus- or minus-cylinder
 * notation — so the caller never needs to know or ask which convention a
 * given prescription was written in.
 */

export const LENS_INDICES = [
  { value: 1.50, label: '1.50 (Standard/CR-39)' },
  { value: 1.56, label: '1.56 (Mid-index)' },
  { value: 1.60, label: '1.60 (Thin)' },
  { value: 1.67, label: '1.67 (High-index)' },
  { value: 1.74, label: '1.74 (Ultra-thin)' },
];

export function sphericalEquivalent(sph, cyl) {
  const s = Number(sph) || 0;
  const c = Number(cyl) || 0;
  return s + c / 2;
}

// R = radius of curvature (mm) for a surface of the given power (D) and index.
// power must be > 0 (use Math.abs before calling).
function radiusOfCurvature(power, index) {
  if (!power) return Infinity;
  return (1000 * (index - 1)) / power;
}

// Exact sag (surface depth, mm) at radius r for a surface of curvature R.
// Returns null if the power/diameter combination is geometrically invalid
// (the requested radius exceeds the surface's own radius of curvature).
export function sag(rMm, power, index) {
  const R = radiusOfCurvature(Math.abs(power), index);
  if (!isFinite(R)) return 0;
  if (rMm >= R) return null;
  return R - Math.sqrt(R * R - rMm * rMm);
}

/**
 * calcMbsAndThickness — the single entry point the UI calls.
 * @param {object} p
 *   frameA, frameB: boxed frame eye size / height (mm)
 *   bridge: DBL, distance between lenses (mm)
 *   patientPd: binocular PD (mm) — split evenly per eye
 *   sph, cyl: prescription (D), cyl in either plus or minus convention
 *   index: refractive index (e.g. 1.50, 1.67)
 *   edgingAllowance: extra mm added to MBS for safe edging (default 2)
 *   minThickness: minimum center thickness (minus lens) or minimum edge
 *     thickness (plus lens) the lab will cut to, in mm (default 1.0)
 * @returns {object} { ed, decentration, mbs, se, lensType, thickness, error }
 */
export function calcMbsAndThickness({
  frameA, frameB, bridge, patientPd, sph, cyl, index,
  edgingAllowance = 2, minThickness = 1.0,
}) {
  const A = Number(frameA), B = Number(frameB), DBL = Number(bridge), PD = Number(patientPd);
  if (!A || !B || !DBL || !PD) {
    return { error: 'Enter frame eye size, height, bridge and patient PD to calculate.' };
  }

  const ed = Math.sqrt(A * A + B * B);
  const framePd = A + DBL;
  const decentration = Math.abs((framePd - PD) / 2);
  const mbs = ed + 2 * decentration + Number(edgingAllowance || 0);

  const se = sphericalEquivalent(sph, cyl);
  const r = ed / 2;
  const idx = Number(index) || 1.50;

  if (se === 0) {
    return { ed, decentration, mbs, se, lensType: 'plano', thickness: { value: Number(minThickness), label: 'uniform (plano)' } };
  }

  const s = sag(r, se, idx);
  if (s === null) {
    return {
      ed, decentration, mbs, se, lensType: se < 0 ? 'minus' : 'plus',
      error: `This power is too strong for a ${Math.round(ed)}mm blank at this index — the surface can't reach that curvature. Try a smaller frame, a higher index, or confirm with your lab.`,
    };
  }

  const lensType = se < 0 ? 'minus' : 'plus';
  const thicknessValue = Number(minThickness) + s;
  return {
    ed, decentration, mbs, se, lensType,
    thickness: { value: thicknessValue, label: lensType === 'minus' ? 'estimated edge thickness' : 'estimated center thickness' },
  };
}

// Same-prescription comparison across all standard indices, for the
// "see how much thinner a higher index gets you" table.
export function compareAcrossIndices({ frameA, frameB, sph, cyl, minThickness = 1.0 }) {
  const A = Number(frameA), B = Number(frameB);
  if (!A || !B) return [];
  const ed = Math.sqrt(A * A + B * B);
  const r = ed / 2;
  const se = sphericalEquivalent(sph, cyl);
  if (se === 0) return [];
  return LENS_INDICES.map(({ value, label }) => {
    const s = sag(r, se, value);
    return { index: value, label, thickness: s === null ? null : Number(minThickness) + s };
  });
}
